import React, { useCallback, useEffect, useState, VFC } from 'react';
import { EventBus } from 'src/common/EventBus';
import useQueryParams from 'src/common/hooks/useQueryParams';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import { Organization } from 'src/common/interfaces/organization.interface';
import { OrganizationInvitedUser } from 'src/common/interfaces/organization/invited-user.interface';
import JourneySlideover from 'src/common/JourneySlideOver';
import { useCurrentUser } from 'src/store/user';
import { apiSendOrganizationInvite } from 'src/utils/journeyApi';
import { withFixedLoadingSpinner } from 'src/common/components/loading-spinner/hooks/with-loading-spinner';
import { DashboardSettingsMembersInviteForm } from './invite-form';
import { DashboardSettingsMembersList, DashboardSettingsMembersListProps } from './list';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';

interface DashboardSettingsMembersSectionComponentProps
  extends Omit<DashboardSettingsMembersListProps, 'currentUserId'> {
  currentOrganizationId: Organization['id'];
}

const SLIDE_OVER_ANIMATION_DURATION_IN_MS = 300;

const DashboardSettingsMembersSectionComponent: VFC<DashboardSettingsMembersSectionComponentProps> = ({
  currentOrganizationId,
  ...props
}) => {
  const params = useQueryParams();
  const inviteParam = params.get('invite');
  const { currentUser } = useCurrentUser();
  const [showInviteMemberPanel, setShowInviteMemberPanel] = useState(Boolean(inviteParam));
  const [showSlideOver, setShowSlideOver] = useState(Boolean(inviteParam));

  useEffect(() => {
    const onMemberInviteClicked = () => {
      setShowInviteMemberPanel(true);
      setTimeout(() => {
        setShowSlideOver(true);
      }, 50);
    };

    EventBus.on('dashboard-settings:show-invite-member-panel', onMemberInviteClicked);
    return () => {
      EventBus.off('dashboard-settings:show-invite-member-panel', onMemberInviteClicked);
    };
  }, []);

  const onInviteMemberPanelClosed = useCallback(() => {
    setShowSlideOver(false);
    setTimeout(() => {
      setShowInviteMemberPanel(false);
    }, SLIDE_OVER_ANIMATION_DURATION_IN_MS);
  }, []);

  const onUserInvitationSubmitted = useCallback(
    (invitedUser: OrganizationInvitedUser | OrganizationUser) => {
      onInviteMemberPanelClosed();
      EventBus.emit('dashboard-settings:success-invite-member', invitedUser);
    },
    [onInviteMemberPanelClosed]
  );

  const onUserInvitationFailed = useCallback(
    (errorMessage: string) => {
      onInviteMemberPanelClosed();
      EventBus.emit('dashboard-settings:fail-invite-member', errorMessage);
    },
    [onInviteMemberPanelClosed]
  );

  const organizationHasQuota = currentUser && currentUser.organization.quota !== null;
  const organizationHasReachedQuota =
    organizationHasQuota && currentUser!.organization.quota! - currentUser!.organization.num_of_billable_users === 0;

  const onInviteMemberFormSubmitted = useCallback(
    async (values: Record<string, string>) => {
      const { email, ...otherParams } = values;
      if (organizationHasReachedQuota) {
        onUserInvitationFailed(
          'You have reached the maximum number of users for your current plan. Please get in touch with support.'
        );
        return;
      }
      try {
        const response = await apiSendOrganizationInvite(currentOrganizationId, email, otherParams);
        onUserInvitationSubmitted(response);
      } catch (err) {
        onUserInvitationFailed((err as ErrorInterface).message);
      }
    },
    [currentOrganizationId, organizationHasReachedQuota, onUserInvitationFailed, onUserInvitationSubmitted]
  );

  if (!currentUser) {
    return null;
  }

  return (
    <>
      <DashboardSettingsMembersList {...props} currentUserId={currentUser.id} />
      {showInviteMemberPanel && (
        <JourneySlideover
          isBedrock
          title='Invite new member'
          open={showSlideOver}
          maxWidth='auto'
          panelClassName='w-[368px]'
          titleClassName='font-brand-serif'
          onClose={onInviteMemberPanelClosed}
        >
          <DashboardSettingsMembersInviteForm onSubmit={onInviteMemberFormSubmitted} />
        </JourneySlideover>
      )}
    </>
  );
};

export const DashboardSettingsMembersSection = withFixedLoadingSpinner<DashboardSettingsMembersSectionComponentProps>(
  DashboardSettingsMembersSectionComponent
);
